import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import PasswordInput from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { User as UserIcon, Lock, Bell, Globe, MapPin, ShieldCheck, LogOut, Trash2, MessageSquare, Camera, Image as ImageIcon } from "lucide-react";

// Defined OUTSIDE Settings() so it keeps identity across renders and inputs keep focus.
const Section = ({ title, icon: Icon, children, testid }) => (
  <Card className="rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7 bg-white" data-testid={testid}>
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center"><Icon size={16} /></div>
      <div className="font-heading font-semibold text-slate-900">{title}</div>
    </div>
    {children}
  </Card>
);

export default function Settings() {
  const { user, loading, logout, setUser } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [busyProfile, setBusyProfile] = useState(false);
  const [busyPhoto, setBusyPhoto] = useState(false);
  const [photoAction, setPhotoAction] = useState(null); // 'upload' | 'remove' | null
  const photoInputRef = React.useRef(null);

  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busyPwd, setBusyPwd] = useState(false);

  const [notif, setNotif] = useState({ new_reports: true, mentions: true, status_changes: true, rewards: true });
  const [geoEnabled, setGeoEnabled] = useState(true);
  const [privacyPublic, setPrivacyPublic] = useState(true);

  // Feedback
  const [fbKind, setFbKind] = useState("suggestion");
  const [fbMsg, setFbMsg] = useState("");
  const [fbBusy, setFbBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    setName(user.name || "");
    setUsername(user.username || "");
    setEmail(user.email || "");
    if (user.notifications) setNotif({ ...notif, ...user.notifications });
    if (typeof user.geolocation_enabled === "boolean") setGeoEnabled(user.geolocation_enabled);
    if (user.privacy && typeof user.privacy.public === "boolean") setPrivacyPublic(user.privacy.public);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  const saveProfile = async () => {
    setBusyProfile(true);
    try {
      const payload = { name, email, language: lang };
      // iter83 — only send username when the user actually changed it (or set it
      // for the first time). Case-insensitive comparison to avoid a needless
      // "username taken" error when only casing differs.
      const current = (user.username || "").toLowerCase();
      const next = (username || "").trim();
      if (next && next.toLowerCase() !== current) payload.username = next;
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      toast.success(t("profile_updated"));
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setBusyProfile(false); }
  };

  // ---------- Profile photo (iter80) ----------
  const backend = process.env.REACT_APP_BACKEND_URL;
  const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      toast.error(t("profile_photo_invalid_type"));
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error(t("profile_photo_too_large"));
      return;
    }
    setBusyPhoto(true);
    setPhotoAction("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // /files/upload returns {file_id, path, url}. We store the FULL absolute
      // URL so the AvatarImage src works both for internal uploads and for
      // legacy Google-provider avatars without any resolver.
      const { data: uploaded } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const pictureUrl = `${backend}/api/files/${uploaded.path}`;
      const { data: me } = await api.patch("/auth/me", { picture: pictureUrl });
      setUser(me);
      toast.success(t("profile_photo_updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("profile_photo_error"));
    } finally {
      setBusyPhoto(false);
      setPhotoAction(null);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    setBusyPhoto(true);
    setPhotoAction("remove");
    try {
      const { data: me } = await api.patch("/auth/me", { picture: "" });
      setUser(me);
      toast.success(t("profile_photo_removed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("profile_photo_error"));
    } finally {
      setBusyPhoto(false);
      setPhotoAction(null);
    }
  };

  const savePassword = async () => {
    if (!curPwd || !newPwd) { toast.error(t("fill_both_fields")); return; }
    if (newPwd.length < 6) { toast.error(t("reset_min_chars")); return; }
    setBusyPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success(t("password_updated"));
      setCurPwd(""); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    finally { setBusyPwd(false); }
  };

  const saveNotif = async (next) => {
    setNotif(next);
    try { await api.patch("/auth/me", { notifications: next }); } catch {}
  };

  const saveGeo = async (val) => {
    setGeoEnabled(val);
    try {
      localStorage.setItem("tc_geo_enabled", String(val));
      await api.patch("/auth/me", { geolocation_enabled: val });
    } catch {}
    if (val) {
      navigator.geolocation?.getCurrentPosition(() => {}, () => toast.error(t("geo_browser_denied")));
    }
  };

  const savePrivacy = async (val) => {
    setPrivacyPublic(val);
    try { await api.patch("/auth/me", { privacy: { public: val } }); } catch {}
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/auth/me");
      localStorage.removeItem("tc_token");
      toast.success(t("account_deleted"));
      nav("/");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };

  const submitFeedback = async () => {
    if (!fbMsg || fbMsg.trim().length < 5) { toast.error(t("feedback_min_words")); return; }
    setFbBusy(true);
    try {
      await api.post("/feedback", { kind: fbKind, message: fbMsg });
      toast.success(t("feedback_thanks"));
      setFbMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || t("feedback_error")); }
    finally { setFbBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="settings-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="settings-back-btn" />
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("nav_settings")}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">{t("settings_title")}</h1>
          <p className="mt-2 text-slate-600">{t("settings_desc")}</p>
        </div>

        <div className="space-y-5">
          <Section title={t("section_profile_photo")} icon={ImageIcon} testid="section-profile-photo">
            <div className="flex items-start gap-5">
              <Avatar className="w-20 h-20 border border-slate-200" data-testid="settings-current-avatar">
                <AvatarImage src={user.picture || undefined} alt={user.name} />
                <AvatarFallback className="bg-emerald-50 text-emerald-700 text-xl font-semibold">
                  {(user.name || "?").trim().slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{t("profile_photo_desc")}</p>
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid="settings-photo-input"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    data-testid="settings-photo-upload"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={busyPhoto}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
                  >
                    <Camera size={14} className="mr-2" />
                    {busyPhoto && photoAction === "upload"
                      ? t("profile_photo_uploading")
                      : user.picture
                        ? t("profile_photo_replace")
                        : t("profile_photo_upload")}
                  </Button>
                  {user.picture ? (
                    <Button
                      type="button"
                      data-testid="settings-photo-remove"
                      onClick={removePhoto}
                      disabled={busyPhoto}
                      variant="outline"
                      className="rounded-full border-red-200 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600"
                    >
                      <Trash2 size={14} className="mr-2" />
                      {busyPhoto && photoAction === "remove" ? t("profile_photo_removing") : t("profile_photo_remove")}
                    </Button>
                  ) : null}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">{t("profile_photo_hint")}</p>
              </div>
            </div>
          </Section>

          <Section title={t("section_profile")} icon={UserIcon} testid="section-profile">
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("name")}</Label>
                <Input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("username_label")}</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-slate-500 font-semibold">@</span>
                  <Input
                    data-testid="settings-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
                    maxLength={30}
                    pattern="[A-Za-z0-9._]{3,30}"
                    className="h-11 rounded-xl"
                    placeholder={t("username_placeholder")}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{t("username_help")}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("email")}</Label>
                <Input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                <p className="text-[11px] text-slate-500 mt-1">{t("email_private_hint")}</p>
              </div>
              <Button data-testid="settings-save-profile" onClick={saveProfile} disabled={busyProfile} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">{t("save")}</Button>
            </div>
          </Section>

          <Section title={t("section_password")} icon={Lock} testid="section-password">
            {user.provider === "google" && !user.has_local_password ? (
              <div className="space-y-4">
                <div className="text-sm text-slate-600 leading-relaxed">{t("set_password_desc")}</div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("new_password_min")}</Label>
                  <PasswordInput testId="set-local-pwd-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <Button
                  data-testid="set-local-password-btn"
                  onClick={async () => {
                    if (newPwd.length < 6) { toast.error(t("reset_min_chars")); return; }
                    try {
                      await api.post("/auth/set-password", { new_password: newPwd });
                      toast.success(t("local_password_set"));
                      setNewPwd("");
                      const { data } = await api.get("/auth/me");
                      setUser(data);
                    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
                  }}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"
                >{t("set_local_password")}</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("current_password")}</Label>
                  <PasswordInput testId="pwd-current" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("new_password_min")}</Label>
                  <PasswordInput testId="pwd-new" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <Button data-testid="settings-save-password" onClick={savePassword} disabled={busyPwd} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">{t("change_password")}</Button>
              </div>
            )}
          </Section>

          <Section title={t("section_notifications")} icon={Bell} testid="section-notifications">
            <div className="space-y-3">
              {[
                { k: "new_reports", label: t("notif_new_reports") },
                { k: "mentions", label: t("notif_mentions") },
                { k: "status_changes", label: t("notif_status_changes") },
                { k: "rewards", label: t("notif_rewards") },
              ].map((row) => (
                <div key={row.k} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-slate-800">{row.label}</span>
                  <Switch data-testid={`notif-${row.k}`} checked={notif[row.k]} onCheckedChange={(v) => saveNotif({ ...notif, [row.k]: v })} />
                </div>
              ))}
            </div>
          </Section>

          <Section title={t("section_language")} icon={Globe} testid="section-language">
            <div className="max-w-xs">
              <Select value={lang} onValueChange={(v) => { setLang(v); api.patch("/auth/me", { language: v }).catch(() => {}); toast.success(t("lang_updated")); }}>
                <SelectTrigger data-testid="settings-language" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="pt">Português</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Section>

          <Section title={t("section_geolocation")} icon={MapPin} testid="section-geolocation">
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm text-slate-700 max-w-lg">
                {t("geo_desc")}
              </div>
              <Switch data-testid="toggle-geolocation" checked={geoEnabled} onCheckedChange={saveGeo} />
            </div>
          </Section>

          <Section title={t("section_privacy")} icon={ShieldCheck} testid="section-privacy">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="text-sm text-slate-700 max-w-lg">
                <b>{t("privacy_public_b")}</b> {t("privacy_public_desc")}
              </div>
              <Switch data-testid="toggle-privacy" checked={privacyPublic} onCheckedChange={savePrivacy} />
            </div>
            <div className="text-xs text-slate-500">
              {t("privacy_footnote")}
            </div>
          </Section>

          <Section title={t("section_feedback")} icon={MessageSquare} testid="section-feedback">
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("feedback_kind")}</Label>
                <Select value={fbKind} onValueChange={setFbKind}>
                  <SelectTrigger data-testid="feedback-kind" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suggestion">{t("feedback_kind_suggestion")}</SelectItem>
                    <SelectItem value="bug">{t("feedback_kind_bug")}</SelectItem>
                    <SelectItem value="improvement">{t("feedback_kind_improvement")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("feedback_message")}</Label>
                <Textarea data-testid="feedback-message" value={fbMsg} onChange={(e) => setFbMsg(e.target.value)} placeholder={t("feedback_message_placeholder")} className="mt-1.5 min-h-[100px] rounded-xl" />
              </div>
              <Button data-testid="feedback-submit" onClick={submitFeedback} disabled={fbBusy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">
                {fbBusy ? t("feedback_submitting") : t("feedback_submit")}
              </Button>
            </div>
          </Section>

          <Section title={t("section_session")} icon={LogOut} testid="section-session">
            <div className="mb-4">
              <Button data-testid="restart-tour-btn" variant="outline" onClick={() => { localStorage.removeItem("tc_tour_v1"); window.dispatchEvent(new Event("tc:open-tour")); }} className="rounded-full hover:bg-emerald-50 hover:text-emerald-800">
                {t("tour_restart")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="settings-logout" onClick={async () => { await logout(); nav("/"); }} variant="outline" className="rounded-full hover:bg-slate-900 hover:text-white">
                <LogOut size={14} className="mr-2" /> {t("logout_label")}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="settings-delete-open" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600">
                    <Trash2 size={14} className="mr-2" /> {t("delete_account")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("delete_account_title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("delete_account_desc")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="settings-delete-cancel">{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction data-testid="settings-delete-confirm" onClick={deleteAccount} className="bg-red-600 hover:bg-red-700 text-white">{t("delete")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
